/**
 * Development Debugging Logger
 * 
 * PURPOSE: Console logging for development debugging and monitoring
 * STORAGE: Browser console only (not persisted)
 * WHEN TO USE: All development debugging, service-level logging, operation timing
 * 
 * This is DIFFERENT from error-logger.ts which persists critical errors to Supabase.
 * Both loggers serve different purposes and should coexist.
 * 
 * Features:
 * - Structured JSON output
 * - Context enrichment (userId, organizationId, etc.)
 * - Child loggers with fixed context
 * - Operation timing
 * - Auto-suppressed in production (only warn/error)
 * 
 * Usage:
 *   logger.info('User logged in', { userId: '123' });
 *   const serviceLogger = logger.child({ service: 'PostsService' });
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  userId?: string;
  organizationId?: string;
  campaignId?: string;
  postId?: string;
  component?: string;
  function?: string;
  operation?: string;
  duration?: number;
  [key: string]: unknown;
}

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  context: LogContext;
  error?: {
    name: string;
    message: string;
    stack?: string;
    code?: string;
  };
  environment: string;
}

class StructuredLogger {
  private isDevelopment: boolean;
  private sessionId: string;

  constructor() {
    this.isDevelopment = import.meta.env.DEV;
    this.sessionId = this.generateSessionId();
  }

  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }

  private shouldLog(level: LogLevel): boolean {
    if (this.isDevelopment) {
      return true;
    }
    // In production, only log warnings and errors
    return level === 'warn' || level === 'error';
  }

  private createLogEntry(
    level: LogLevel,
    message: string,
    context: LogContext,
    error?: Error | unknown
  ): LogEntry {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      context: {
        ...context,
        sessionId: this.sessionId,
      },
      environment: this.isDevelopment ? 'development' : 'production',
    };

    if (error) {
      if (error instanceof Error) {
        entry.error = {
          name: error.name,
          message: error.message,
          stack: this.isDevelopment ? error.stack : undefined,
          code: 'code' in error ? (error as Error & { code?: string }).code : undefined,
        };
      } else {
        entry.error = {
          name: 'UnknownError',
          message: String(error),
        };
      }
    }

    return entry;
  }

  private formatForConsole(entry: LogEntry): string {
    const { timestamp, level, message, context, error } = entry;
    const contextStr = Object.keys(context).length > 0 
      ? ` | ${JSON.stringify(context)}` 
      : '';
    const errorStr = error ? ` | Error: ${error.message}` : '';
    return `[${timestamp}] [${level.toUpperCase()}] ${message}${contextStr}${errorStr}`;
  }

  debug(message: string, context: LogContext = {}): void {
    if (!this.shouldLog('debug')) return;
    const entry = this.createLogEntry('debug', message, context);
    console.debug(this.formatForConsole(entry));
  }

  info(message: string, context: LogContext = {}): void {
    if (!this.shouldLog('info')) return;
    const entry = this.createLogEntry('info', message, context);
    console.info(this.formatForConsole(entry));
  }

  warn(message: string, context: LogContext = {}): void {
    if (!this.shouldLog('warn')) return;
    const entry = this.createLogEntry('warn', message, context);
    console.warn(this.formatForConsole(entry));
  }

  error(message: string, error?: Error | unknown, context: LogContext = {}): void {
    if (!this.shouldLog('error')) return;
    const entry = this.createLogEntry('error', message, context, error);
    console.error(this.formatForConsole(entry));
    
    // In production, you could send to external logging service
    // if (!this.isDevelopment) {
    //   this.sendToExternalService(entry);
    // }
  }

  /**
   * Time an operation
   */
  async time<T>(
    label: string,
    operation: () => Promise<T>,
    context: LogContext = {}
  ): Promise<T> {
    const start = performance.now();
    try {
      const result = await operation();
      const duration = Math.round(performance.now() - start);
      this.info(`${label} completed`, { ...context, duration });
      return result;
    } catch (error) {
      const duration = Math.round(performance.now() - start);
      this.error(`${label} failed`, error, { ...context, duration });
      throw error;
    }
  }

  /**
   * Create a child logger with fixed context
   */
  child(fixedContext: LogContext): ChildLogger {
    return new ChildLogger(this, fixedContext);
  }
}

class ChildLogger {
  constructor(
    private parent: StructuredLogger,
    private fixedContext: LogContext
  ) {}

  debug(message: string, context: LogContext = {}): void {
    this.parent.debug(message, { ...this.fixedContext, ...context });
  }

  info(message: string, context: LogContext = {}): void {
    this.parent.info(message, { ...this.fixedContext, ...context });
  }

  warn(message: string, context: LogContext = {}): void {
    this.parent.warn(message, { ...this.fixedContext, ...context });
  }

  error(message: string, error?: Error | unknown, context: LogContext = {}): void {
    this.parent.error(message, error, { ...this.fixedContext, ...context });
  }

  async time<T>(
    label: string,
    operation: () => Promise<T>,
    context: LogContext = {}
  ): Promise<T> {
    return this.parent.time(label, operation, { ...this.fixedContext, ...context });
  }
}

// Singleton instance
export const logger = new StructuredLogger();

/**
 * Usage examples:
 * 
 * // Simple logging
 * logger.info('User logged in', { userId: '123' });
 * 
 * // With error
 * logger.error('Failed to create post', error, { postId: '456' });
 * 
 * // Timing operations
 * const posts = await logger.time('Fetch posts', async () => {
 *   return await PostsService.getAll({ organizationId: 'org-123' });
 * }, { organizationId: 'org-123' });
 * 
 * // Child logger with fixed context
 * const serviceLogger = logger.child({ component: 'PostsService' });
 * serviceLogger.info('Creating post', { userId: '123' });
 */
